// ChatWidget.jsx - Hotel Chatbot Component with WhatsApp handoff
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Minimize2, PhoneCall, ShieldCheck, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// "Was this answered?" strings (7 languages).
const FB = {
  en: { fromQa: 'Reception’s published answer', q: 'Was this answered?', yes: 'Yes', no: 'No', thanksYes: 'Thanks!', thanksNo: 'Thanks — reception will answer this question and publish it for other guests.' },
  me: { fromQa: 'Objavljen odgovor recepcije', q: 'Da li je ovo odgovorilo na pitanje?', yes: 'Da', no: 'Ne', thanksYes: 'Hvala!', thanksNo: 'Hvala — recepcija će odgovoriti na ovo pitanje i objaviti ga za druge goste.' },
  sq: { fromQa: 'Përgjigja e publikuar e recepsionit', q: 'A u përgjigj kjo?', yes: 'Po', no: 'Jo', thanksYes: 'Faleminderit!', thanksNo: 'Faleminderit — recepsioni do t’i përgjigjet kësaj pyetjeje dhe do ta publikojë për mysafirët e tjerë.' },
  de: { fromQa: 'Veröffentlichte Antwort der Rezeption', q: 'Wurde Ihre Frage beantwortet?', yes: 'Ja', no: 'Nein', thanksYes: 'Danke!', thanksNo: 'Danke – die Rezeption beantwortet diese Frage und veröffentlicht sie für andere Gäste.' },
  ru: { fromQa: 'Опубликованный ответ ресепшен', q: 'Ответ помог?', yes: 'Да', no: 'Нет', thanksYes: 'Спасибо!', thanksNo: 'Спасибо — ресепшен ответит на этот вопрос и опубликует ответ для других гостей.' },
  pl: { fromQa: 'Opublikowana odpowiedź recepcji', q: 'Czy to odpowiedziało na pytanie?', yes: 'Tak', no: 'Nie', thanksYes: 'Dziękujemy!', thanksNo: 'Dziękujemy — recepcja odpowie na to pytanie i opublikuje je dla innych gości.' },
  hu: { fromQa: 'A recepció közzétett válasza', q: 'Megválaszoltuk a kérdést?', yes: 'Igen', no: 'Nem', thanksYes: 'Köszönjük!', thanksNo: 'Köszönjük — a recepció megválaszolja ezt a kérdést és közzéteszi más vendégek számára.' },
};

const AnsweredRow = ({ msg, t, onAnswer }) => {
  if (msg.feedback === 'yes') return <p className="text-[11px] text-emerald-700 mt-1.5" data-testid={`chat-fb-thanks-${msg.id}`}>{t.thanksYes}</p>;
  if (msg.feedback === 'no') return <p className="text-[11px] text-slate-600 mt-1.5" data-testid={`chat-fb-thanks-${msg.id}`}>{t.thanksNo}</p>;
  return (
    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500" data-testid={`chat-fb-${msg.id}`}>
      <span>{t.q}</span>
      <button type="button" onClick={() => onAnswer(true)} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 hover:bg-emerald-50 hover:text-emerald-700" data-testid={`chat-fb-yes-${msg.id}`}><ThumbsUp className="w-3 h-3" /> {t.yes}</button>
      <button type="button" onClick={() => onAnswer(false)} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 hover:bg-red-50 hover:text-red-700" data-testid={`chat-fb-no-${msg.id}`}><ThumbsDown className="w-3 h-3" /> {t.no}</button>
    </div>
  );
};

// Words / phrases across our 7 languages that indicate the guest wants a human.
const HANDOFF_KEYWORDS = [
  // en
  'book', 'reserve', 'human', 'person', 'staff', 'manager', 'whatsapp', 'call',
  'speak to', 'talk to', 'real person', 'agent', 'someone',
  // me / sq
  'rezerv', 'osob', 'čovjek', 'čovek', 'menad', 'zovi', 'poziv',
  'njeri', 'personi', 'thirr', 'flas me',
  // de
  'buch', 'reserv', 'mensch', 'person', 'sprechen', 'anruf', 'mitarbeiter',
  // ru
  'заказ', 'бронир', 'человек', 'сотрудник', 'позвон', 'звон', 'настоящ',
  // pl
  'zarezerw', 'człowiek', 'osob', 'zadzwo', 'pracownik', 'rezerwacj',
  // hu
  'foglal', 'ember', 'személy', 'hív', 'ügynök', 'munkatárs',
];

const ChatWidget = () => {
  const { hotelInfo } = useHotel();
  const { t, lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hello! 👋 Welcome to Hotel La Rosa Negra. I'm your virtual concierge. How can I help you today? Feel free to ask about our rooms, amenities, or anything else!",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [handoffDismissed, setHandoffDismissed] = useState(false);
  const [mode, setMode] = useState('ai');
  const [contactDone, setContactDone] = useState(false);
  const [contactDismissed, setContactDismissed] = useState(false);
  const [contact, setContact] = useState('');
  const lastPollRef = useRef(null);
  const typingSentRef = useRef(0);
  const [staffTyping, setStaffTyping] = useState(false);
  const [ratePending, setRatePending] = useState(false);
  const [rated, setRated] = useState(false);
  const [rateComment, setRateComment] = useState('');
  const sendRating = async (rating) => {
    if (!sessionId) return;
    try {
      await fetch(`${API_URL}/api/chat/${sessionId}/rate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rating, comment: rateComment.trim() || null }) });
    } catch { /* ignore */ }
    setRated(true); setRatePending(false);
  };
  const messagesEndRef = useRef(null);
  const pingTyping = () => {
    if (!sessionId || Date.now() - typingSentRef.current < 3000) return;
    typingSentRef.current = Date.now();
    fetch(`${API_URL}/api/chat/${sessionId}/typing`, { method: 'POST' }).catch(() => {});
  };

  // Live takeover: poll for staff replies every 5 s while the widget is open.
  useEffect(() => {
    if (!sessionId || !isOpen) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const q = lastPollRef.current ? `?after=${encodeURIComponent(lastPollRef.current)}` : '';
        const r = await fetch(`${API_URL}/api/chat/${sessionId}/updates${q}`);
        const d = await r.json();
        if (!alive) return;
        lastPollRef.current = d.now;
        setMode(d.mode);
        setStaffTyping(Boolean(d.staff_typing));
        if (d.rate_pending && !rated) setRatePending(true);
        if (d.ended) setMode('ai');
        if (d.has_contact) setContactDone(true);
        if (d.messages?.length) setMessages((prev) => [...prev, ...d.messages.map((m) => ({ type: 'staff', text: m.text, by: m.by, timestamp: new Date(m.timestamp) }))]);
      } catch { /* offline — retry next tick */ }
    };
    tick();
    const id = setInterval(tick, mode === 'human' ? 2500 : 5000);
    return () => { alive = false; clearInterval(id); };
  }, [sessionId, isOpen, mode]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!sessionId || !contact.trim()) return;
    const isEmail = contact.includes('@');
    try {
      const r = await fetch(`${API_URL}/api/chat/${sessionId}/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(isEmail ? { email: contact.trim() } : { phone: contact.trim() }) });
      if (r.ok) { setContactDone(true); setMessages((prev) => [...prev, { type: 'staff', by: 'Reception', text: t('chatContact.thanks'), timestamp: new Date() }]); }
    } catch { /* ignore */ }
  };
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Show WhatsApp handoff either after 3 user turns OR immediately on
  // handoff-intent keywords ("book", "reservation", "human"…).
  const userMsgCount = useMemo(
    () => messages.filter(m => m.type === 'user').length,
    [messages]
  );

  const handoffTriggered = useMemo(() => {
    if (handoffDismissed) return false;
    if (userMsgCount >= 3) return true;
    const lastUserMsg = [...messages].reverse().find(m => m.type === 'user');
    if (!lastUserMsg) return false;
    const txt = lastUserMsg.text.toLowerCase();
    return HANDOFF_KEYWORDS.some(k => txt.includes(k));
  }, [messages, userMsgCount, handoffDismissed]);

  const handleWhatsAppHandoff = () => {
    // Compose a short conversation summary for the receptionist.
    const lastMessages = messages.slice(-6);
    const transcript = lastMessages
      .map(m => `${m.type === 'user' ? '👤' : '🤖'} ${m.text}`)
      .join('\n');
    const prefix = t('handoff.prefix');
    const body = `${prefix}\n\n---\n${transcript}`.slice(0, 1200);
    const number = (hotelInfo?.whatsapp || '38269533984').replace(/\D/g, '');
    const url = `https://wa.me/${number}?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const FB_T = FB[lang] || FB.en;
  const sendFeedback = (msg, yes) => {
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, feedback: yes ? 'yes' : 'no' } : m)));
    if (!sessionId) return;
    fetch(`${API_URL}/api/chat/${sessionId}/qa-feedback`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: msg.userText, answered: yes, bot_response: msg.text, qa_id: msg.qaId, lang }),
    }).catch(() => {});
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');

    setMessages(prev => [...prev, {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    }]);

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          lang
        })
      });

      const data = await response.json();

      if (data.session_id) {
        setSessionId(data.session_id);
      }

      if (data.human) {
        setMode('human');
        return;
      }
      setMessages(prev => [...prev, {
        type: 'bot',
        text: data.response,
        timestamp: new Date(),
        id: `b-${Date.now()}`,
        qaId: data.qa_id || null,
        askFeedback: !!data.ask_feedback,
        userText: userMessage,
        feedback: null,
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "I apologize, but I'm having trouble connecting. Please try again or contact us at +382 69 533 984.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    "What rooms are available?",
    "What are your amenities?",
    "How do I book?",
    "Where are you located?"
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
    setTimeout(() => handleSend(), 100);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-white z-50 hover:scale-110"
        aria-label="Open chat"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-72' : 'w-96'
      }`}
      data-testid="chat-widget"
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Hotel Concierge</h3>
                <p className="text-xs text-orange-100">La Rosa Negra</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={msg.timestamp ? `${msg.timestamp}-${idx}` : `msg-${idx}`}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.type === 'user'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {msg.type === 'user' ? <User className="w-4 h-4" /> : msg.type === 'staff' ? <PhoneCall className="w-4 h-4 text-green-700" /> : <Bot className="w-4 h-4" />}
                    </div>
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      msg.type === 'user'
                        ? 'bg-orange-500 text-white rounded-br-md'
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
                    }`}>
                      {msg.type === 'staff' && <p className="text-[10px] uppercase tracking-wide text-green-700 font-bold mb-0.5">{msg.by || 'Reception'}</p>}
                      {msg.qaId && <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold mb-0.5 inline-flex items-center gap-1" data-testid="chat-qa-label"><ShieldCheck className="w-3 h-3" /> {FB_T.fromQa}</p>}
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                      {msg.askFeedback && <AnsweredRow msg={msg} t={FB_T} onAnswer={(yes) => sendFeedback(msg, yes)} />}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-2 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions - show only at start */}
            {messages.length === 1 && (
              <div className="px-4 pb-2 bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleQuickQuestion(q)}
                      className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* WhatsApp handoff card - appears after 3 user turns OR on keyword */}
            {mode === 'human' && (
              <div className="mx-4 mb-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="chat-human-mode">
                <PhoneCall className="w-3.5 h-3.5" /> {t('live.replying')}
              </div>
            )}
            {(ratePending || rated) && (
              <div className="mx-4 mb-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="chat-rate-prompt">
                {rated ? <span className="text-emerald-700 font-semibold" data-testid="chat-rate-thanks">{t('rate.thanks')}</span> : (
                  <>
                    <p className="text-slate-700 font-semibold mb-1">{t('rate.ask')}</p>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => sendRating('up')} className="px-3 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold" data-testid="chat-rate-up">👍</button>
                      <button type="button" onClick={() => sendRating('down')} className="px-3 py-1 rounded-full bg-red-100 hover:bg-red-200 text-red-800 font-bold" data-testid="chat-rate-down">👎</button>
                      <input value={rateComment} onChange={(e) => setRateComment(e.target.value)} placeholder={t('rate.comment')} className="flex-1 border rounded px-2 py-1 text-xs" data-testid="chat-rate-comment" />
                    </div>
                  </>
                )}
              </div>
            )}
            {staffTyping && (
              <div className="mx-4 mb-2 text-xs text-gray-500 italic flex items-center gap-1" data-testid="chat-staff-typing">
                <span className="inline-flex gap-0.5"><span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" /><span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" /></span> {t('live.typing')}
              </div>
            )}
            {sessionId && !contactDone && !contactDismissed && userMsgCount >= 2 && (
              <form onSubmit={submitContact} className="mx-4 mb-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs relative" data-testid="chat-contact-prompt">
                <button type="button" onClick={() => setContactDismissed(true)} className="absolute top-1 right-1 text-orange-400 hover:text-orange-700" aria-label="Dismiss" data-testid="chat-contact-dismiss"><X className="w-3 h-3" /></button>
                <p className="text-orange-900 mb-1">{t('chatContact.ask')}</p>
                <div className="flex gap-1">
                  <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="name@mail.com / +382…" className="flex-1 border rounded px-2 py-1 text-xs" data-testid="chat-contact-input" />
                  <button type="submit" className="bg-orange-500 text-white rounded px-2 py-1 text-xs font-semibold" data-testid="chat-contact-submit">OK</button>
                </div>
              </form>
            )}
            {handoffTriggered && (
              <div
                className="mx-4 mb-3 rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-3 shadow-sm relative"
                data-testid="chat-whatsapp-handoff"
              >
                <button
                  onClick={() => setHandoffDismissed(true)}
                  className="absolute top-1.5 right-1.5 p-1 text-green-700/60 hover:text-green-800 hover:bg-green-100 rounded-full transition-colors"
                  aria-label="Dismiss WhatsApp handoff"
                  data-testid="chat-whatsapp-dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <p className="text-xs text-green-900 font-medium mb-2 pr-5">
                  {t('handoff.prompt')}
                </p>
                <button
                  onClick={handleWhatsAppHandoff}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold text-sm px-4 py-2.5 rounded-lg hover:brightness-105 shadow-md hover:shadow-lg transition-all"
                  data-testid="chat-whatsapp-handoff-button"
                >
                  <PhoneCall className="w-4 h-4" />
                  {t('handoff.button')}
                </button>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); pingTyping(); }}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="chat-send-button"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
